// The invite-code field of /welcome (WEB_PLAN.md §5.2 "Have a code?"; NoCompanyView.inviteCodeCard). The rules for
// what the field shows (`cleanInviteCodeInput`) and how the server reads a code (`normalizeInviteCode`) are domain/team's;
// this adds what the screen needs around them: the code as sent, when Continue turns on, reading a shared link's
// `#code=…`, and keeping the caret in place when the field rewrites what was typed. Pure (unit-tested).

import { cleanInviteCodeInput, INVITE_CODE_LENGTH, inviteCodeFromHash } from '@/domain/team';

/** NoCompanyView.normalizedCode: the typed code without dashes (the server normalizes case, O→0, I/L→1 itself). */
export function codeForServer(code: string): string {
  return code.replace(/-/g, '');
}

/** Continue is on once all 12 characters are there (NoCompanyView.codeIsComplete). */
export function isCodeComplete(code: string): boolean {
  return codeForServer(code).length === INVITE_CODE_LENGTH;
}

/**
 * A link `https://groundteam.netlify.app/welcome#code=ABCD-EFGH-JKMN` (domain/team `inviteLink`): the code for the field,
 * and the fragment without it. The code works like a password, so the screen puts the remaining fragment back in the
 * address bar (history.replaceState) instead of leaving the code in the URL and the history.
 */
export function readInviteLink(hash: string): { code: string | null; hash: string } {
  const code = inviteCodeFromHash(hash);
  if (code === null) return { code: null, hash };
  const params = new URLSearchParams(hash.replace(/^#/, ''));
  params.delete('code');
  const rest = params.toString();
  return { code, hash: rest ? `#${rest}` : '' };
}

const CODE_CHAR = /^[A-Za-z0-9]$/;

/**
 * Where the caret goes after `cleanInviteCodeInput` rewrote the field (upper-cased, dropped a character, grouped the
 * full code): right after the same number of code characters as before it, and after a dash the person just typed.
 * Without this, typing in the middle of the code would throw the caret to the end on every key.
 */
export function caretAfterClean(typed: string, caret: number, cleaned: string): number {
  const before = Array.from(typed.slice(0, Math.max(0, Math.min(caret, typed.length))));
  let wanted = 0;
  for (const ch of before) if (CODE_CHAR.test(ch)) wanted += 1;
  wanted = Math.min(wanted, INVITE_CODE_LENGTH);
  let pos = 0;
  let seen = 0;
  while (pos < cleaned.length && seen < wanted) {
    if (CODE_CHAR.test(cleaned.charAt(pos))) seen += 1;
    pos += 1;
  }
  if (before.length > 0 && before[before.length - 1] === '-') {
    while (pos < cleaned.length && cleaned.charAt(pos) === '-') pos += 1;
  }
  return pos;
}

/**
 * What the field holds after the person typed or pasted `raw`: `cleanInviteCodeInput`, except that a pasted invite
 * link (or its "#code=…" part) gives its code instead of the first 12 letters of the address.
 */
export function cleanCodeField(raw: string): string {
  const link = /(?:^|[#?&])code=([^&\s]+)/i.exec(raw);
  if (link?.[1]) {
    let value = link[1];
    try {
      value = decodeURIComponent(value);
    } catch {
      // Keep it as pasted.
    }
    return cleanInviteCodeInput(value);
  }
  return cleanInviteCodeInput(raw);
}
